<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentsAPI from 'dashboard/api/agents';
import TeamsAPI from 'dashboard/api/teams';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(3)
);

// The onboarding wizard only ever guides the account's first team — creating
// more teams happens later in Settings. `team.id` doubles as "has this been
// created for real yet" the same way a row's `id` does in Step02Agents.
const team = ref({
  id: null,
  name: '',
  description: '',
  allowAutoAssign: true,
});
const teamError = ref(null);

const agents = ref([]);
const selectedMemberIds = ref([]);

const isValid = computed(
  () => !!team.value.name.trim() && selectedMemberIds.value.length > 0
);

const roleLabel = value =>
  ({
    agent: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    administrator: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
  })[value];

const teamFromApi = data => ({
  id: data.id,
  name: data.name,
  description: data.description || '',
  allowAutoAssign: data.allow_auto_assign,
});

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 3])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const applyMembers = async teamId => {
  if (!selectedMemberIds.value.length) return;
  await TeamsAPI.addAgents({
    teamId,
    agentsList: selectedMemberIds.value,
  });
};

const createTeam = async () => {
  try {
    const { data } = await TeamsAPI.create({
      name: team.value.name,
      description: team.value.description,
      allow_auto_assign: team.value.allowAutoAssign,
    });
    team.value.id = data.id;
    teamError.value = null;
    await applyMembers(data.id);
    emit('update:can-advance', isValid.value);
    await markStepCompleted();
  } catch (error) {
    teamError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_3.CREATE_ERROR');
  }
};

const updateTeam = async payload => {
  try {
    await TeamsAPI.update(team.value.id, payload);
    teamError.value = null;
  } catch (error) {
    teamError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_3.UPDATE_ERROR');
  }
};

// Rows are saved as soon as they become valid (on blur), same as
// Step02Agents — no separate "Save" button in the design.
const attemptSaveName = () => {
  if (!team.value.name.trim()) return;
  if (team.value.id) {
    updateTeam({ name: team.value.name });
  } else {
    createTeam();
  }
};

const attemptSaveDescription = () => {
  if (team.value.id) updateTeam({ description: team.value.description });
};

const onAllowAutoAssignChange = value => {
  team.value.allowAutoAssign = value;
  if (team.value.id) updateTeam({ allow_auto_assign: value });
};

const toggleMember = async agentId => {
  const nextIds = selectedMemberIds.value.includes(agentId)
    ? selectedMemberIds.value.filter(id => id !== agentId)
    : [...selectedMemberIds.value, agentId];

  if (!team.value.id) {
    selectedMemberIds.value = nextIds;
    emit('update:can-advance', isValid.value);
    return;
  }

  try {
    await TeamsAPI.updateAgents({ teamId: team.value.id, agentsList: nextIds });
    selectedMemberIds.value = nextIds;
    teamError.value = null;
    emit('update:can-advance', isValid.value);
  } catch (error) {
    teamError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_3.UPDATE_ERROR');
  }
};

// Re-syncs when the team is created/edited from another screen (e.g.
// Settings > Teams) so both views stay live. An in-progress, not-yet-created
// local draft is left alone until the real team shows up.
const refreshFromServer = async () => {
  try {
    const { data } = await TeamsAPI.get();
    const realTeam = data[0];
    if (!realTeam) {
      // The team was deleted elsewhere — only reset if we were showing a
      // real one; an in-progress, not-yet-created draft has nothing to lose.
      if (team.value.id) {
        team.value = {
          id: null,
          name: '',
          description: '',
          allowAutoAssign: true,
        };
        selectedMemberIds.value = [];
        emit('update:can-advance', false);
      }
      return;
    }

    team.value = teamFromApi(realTeam);
    const { data: members } = await TeamsAPI.getAgents({
      teamId: realTeam.id,
    });
    selectedMemberIds.value = members.map(member => member.id);
    emit('update:can-advance', isValid.value);
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data: agentsList } = await AgentsAPI.get();
    agents.value = agentsList;

    const { data: teams } = await TeamsAPI.get();
    const realTeam = teams[0];
    if (realTeam) {
      team.value = teamFromApi(realTeam);
      const { data: members } = await TeamsAPI.getAgents({
        teamId: realTeam.id,
      });
      selectedMemberIds.value = members.map(member => member.id);
    }

    emit('update:can-advance', isValid.value);
    if (realTeam && isValid.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.TEAMS_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.TEAMS_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_3.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/415-times-agrupando-atendentes-por-responsabilidade"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_3.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_3.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_3.DESCRIPTION') }}</p>

      <div class="flex flex-col w-full max-w-2xl gap-4">
        <Input
          v-model="team.name"
          custom-input-class="no-margin"
          :label="t('ONBOARDING.STEP_3.NAME_LABEL')"
          :placeholder="t('ONBOARDING.STEP_3.NAME_PLACEHOLDER')"
          @blur="attemptSaveName"
        />
        <Input
          v-model="team.description"
          custom-input-class="no-margin"
          :label="t('ONBOARDING.STEP_3.DESCRIPTION_LABEL')"
          :placeholder="t('ONBOARDING.STEP_3.DESCRIPTION_PLACEHOLDER')"
          @blur="attemptSaveDescription"
        />
        <label class="flex items-center gap-2">
          <Checkbox
            :model-value="team.allowAutoAssign"
            @update:model-value="onAllowAutoAssignChange"
          />
          <span class="text-n-slate-12">{{
            t('ONBOARDING.STEP_3.ALLOW_AUTO_ASSIGN')
          }}</span>
        </label>

        <p v-if="teamError" class="text-sm text-n-ruby-9">{{ teamError }}</p>

        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('ONBOARDING.STEP_3.MEMBERS_LABEL') }}
          </p>
          <label
            v-for="agent in agents"
            :key="agent.id"
            class="flex items-center gap-3 p-3 border rounded-lg border-n-weak"
          >
            <Checkbox
              :model-value="selectedMemberIds.includes(agent.id)"
              @update:model-value="() => toggleMember(agent.id)"
            />
            <span class="font-medium text-n-slate-12">{{ agent.name }}</span>
            <span class="text-sm text-n-slate-9">{{ agent.email }}</span>
            <span class="text-sm text-n-slate-10 ms-auto">{{
              roleLabel(agent.role)
            }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
